'use client'

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import { useClickOutside } from '../../hooks/useClickOutside'
import { useEscapeKey } from '../../hooks/useEscapeKey'
import { useOverflowCount } from '../../hooks/useOverflowCount'
import ReactDOM from 'react-dom'

import { Box, chakra } from '@chakra-ui/react'

import { tokenVar } from '../../theme/tokenVar'
import type { ColorScheme } from '../../types'
import { Chip } from '../Chip/Chip'
import { ChevronDownIcon } from '../Icon/ChevronDownIcon'
import { CloseIcon } from '../Icon/CloseIcon'
import { TypeaheadSearch } from '../TypeaheadSearch/TypeaheadSearch'
import type { CheckboxOption } from '../TypeaheadSearch/TypeaheadSearch'
import {
  checkboxOptionRowStyles,
  checkboxStyles,
  primaryActionHeaderStyles,
} from '../TypeaheadSearch/TypeaheadSearch.styles'
import {
  chevronStyle,
  chipWrapperStyle,
  chipsAreaStyles,
  containerStyles,
  controlButtonStyles,
  controlsStyles,
  inputLabelStyles,
  inputRowStyles,
  placeholderStyles,
} from './MultiSelectInput.styles'
import { OverflowBadge } from './OverflowBadge'

const CHIP_GAP = 6

export type MultiSelectOption = {
  value: string
  label: string
  /** Secondary line under the label in the dropdown (e.g. a facility's short name + address). */
  subtitle?: string
  /** Tertiary, italicized line under the subtitle (e.g. a "matched by X" cue). */
  caption?: string
}

export type MultiSelectInputProps = {
  /**
   * Selected items shown as dismissible chips.
   * Plain mode: display labels. Searchable mode: option values — labels are
   * resolved from `options`.
   */
  selected: string[]
  /** Full selected options used to resolve chip labels outside the current options page. */
  selectedOptions?: MultiSelectOption[]
  /** Called when the user clicks the × on an individual chip. */
  onRemove: (value: string) => void
  /** Called when the user clicks the clear-all button. */
  onClear: () => void
  /**
   * Called when the user clicks the container or chevron to open/close the
   * dropdown. Optional in searchable mode, where open state is managed
   * internally.
   */
  onToggle?: () => void
  /** Controls the chevron rotation to indicate open state (plain mode). */
  isOpen?: boolean
  /** Shown when `selected` is empty. */
  placeholder?: string
  /** Small inset label rendered at the top of the input border. */
  label?: string
  colorScheme?: ColorScheme
  /**
   * When true, renders a built-in searchable dropdown of `options` below the
   * input. `selected` is treated as option values and open state is managed
   * internally.
   */
  searchable?: boolean
  /** Options for the searchable dropdown. Required when `searchable`. */
  options?: MultiSelectOption[]
  /**
   * Called when a dropdown option is toggled on or off (searchable mode).
   * Distinct from `onToggle`, which opens/closes the dropdown.
   */
  onToggleOption?: (value: string) => void
  /** Called when the search query changes (e.g. for server-side filtering). */
  onQueryChange?: (query: string) => void
  /**
   * 'internal' (default) re-filters `options` by label against the typed
   * query — the right choice when `options` is the full, unfiltered list.
   * Pass 'external' when the caller already narrowed `options` server-side
   * (e.g. a facility search that also matches by address, where the typed
   * query wouldn't appear in every matching label) — otherwise a genuine
   * server-side match gets silently re-filtered away.
   */
  filterMode?: 'internal' | 'external'
  /** Placeholder for the search input inside the dropdown. */
  searchPlaceholder?: string
  /** Text shown when no options match the search query. */
  noMatchLabel?: string
  /**
   * Muted uppercase heading rendered above the search input inside the
   * dropdown (e.g. 'Search cases').
   */
  intentLabel?: string
  /**
   * Muted uppercase heading rendered above the matched results inside the
   * dropdown (e.g. 'Select cases'). Passed through to TypeaheadSearch.
   */
  resultsHeader?: string
  /** Maximum number of options to show in the dropdown. Unlimited by default. */
  maxResults?: number
  /**
   * Where the searchable dropdown opens relative to the input.
   * Use 'top' when the input sits near the bottom of its container.
   */
  dropdownPlacement?: 'bottom' | 'top'
  /**
   * Pinned option rendered at the top of the dropdown, above the search
   * section (e.g. 'All guards'). It is never filtered by the search query.
   * Acts as a select-all: while it is selected, every option in the dropdown
   * displays as checked.
   */
  defaultOption?: MultiSelectOption
  /**
   * Muted uppercase heading rendered above the pinned default option
   * (e.g. 'Select All Guards').
   */
  defaultOptionHeader?: string
  /** Maximum height of the dropdown list; scrolls beyond it. */
  maxDropdownHeight?: string | number
  /**
   * Custom content rendered inside the positioned dropdown instead of the
   * built-in searchable option list (e.g. a radio group or a checkbox filter
   * panel). Open state is managed internally and the dropdown closes on
   * outside click and Escape, exactly like searchable mode. Takes precedence
   * over `searchable`. `options` still resolves chip labels for `selected`
   * values.
   */
  panelContent?: React.ReactNode
  /**
   * Render the dropdown in a `document.body` portal (position: fixed, anchored
   * to the input) instead of inline. Use when an ancestor clips overflow — e.g.
   * inside a dark Modal whose clip-path/overflow would otherwise cut it off.
   * Recomputes on scroll/resize.
   */
  usePortal?: boolean
  /**
   * When true, the input is non-interactive: the dropdown cannot open and
   * chips cannot be removed or cleared. Same muted treatment as TextInput.
   * In plain controlled mode this only masks the parent-owned `isOpen` —
   * reset that state when disabling, or the dropdown reopens the moment
   * `disabled` clears.
   */
  disabled?: boolean
  /** Test id rendered on the trigger element. */
  'data-testid'?: string
}

export const MultiSelectInput = ({
  selected,
  selectedOptions = [],
  onRemove,
  onClear,
  onToggle,
  isOpen = false,
  placeholder = 'Select…',
  label,
  colorScheme = 'dark',
  searchable = false,
  options = [],
  onToggleOption,
  onQueryChange,
  filterMode = 'internal',
  searchPlaceholder = 'Search…',
  noMatchLabel = 'No results',
  intentLabel,
  resultsHeader,
  maxResults,
  dropdownPlacement = 'bottom',
  defaultOption,
  defaultOptionHeader,
  maxDropdownHeight,
  panelContent,
  usePortal = false,
  disabled = false,
  'data-testid': dataTestId,
}: MultiSelectInputProps) => {
  // Non-renderable values (undefined/null/false, e.g. `cond ? <X/> : null`)
  // must not activate panel mode, or the dropdown opens empty with the
  // built-in list suppressed.
  const hasPanel = panelContent != null && panelContent !== false
  // Panel mode shares searchable mode's open-state ownership: internal state,
  // wrapper click-outside, and Escape.
  const isManagedOpen = searchable || hasPanel
  const [isInternalOpen, setIsInternalOpen] = useState(false)
  // A disabled input is never presented as open — this also covers plain
  // controlled mode, where the open state belongs to the parent's `isOpen`.
  const isDropdownOpen = (isManagedOpen ? isInternalOpen : isOpen) && !disabled

  // Closing the dropdown also resets the parent's search query so a stale
  // filter doesn't persist into the next open (the input itself remounts).
  const closeDropdown = useCallback(() => {
    setIsInternalOpen(false)
    onQueryChange?.('')
  }, [onQueryChange])

  // Register managed dropdowns in the shared Escape stack so they dismiss
  // before a containing Modal or Drawer handles the same keypress.
  useEscapeKey(closeDropdown, isManagedOpen && isDropdownOpen)

  // A dropdown open at the moment the input becomes disabled (e.g. a form
  // disabling its fields while a mutation is in flight) must not keep its
  // options clickable — close it.
  useEffect(() => {
    if (disabled) setIsInternalOpen(false)
  }, [disabled])

  // Own the click-outside for the searchable dropdown at the wrapper level.
  // TypeaheadSearch's internal dismiss must not be used here: the trigger bar
  // is outside TypeaheadSearch, so its mousedown dismissal would close the
  // dropdown and the subsequent click toggle would immediately reopen it.
  //
  // The close is deferred a tick: useClickOutside fires on mousedown, and
  // unmounting the dropdown (which holds the focused search input) during the
  // mousedown disrupts the browser's default focus action on the clicked
  // element — e.g. clicking another text input would not focus it until a
  // second click.
  // The timer id is tracked so a pending close is cancelled on unmount —
  // otherwise the deferred callback could call onQueryChange('') after
  // teardown (e.g. when the outside click also closes a containing modal).
  const wrapperRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  // Portalled dropdown lives outside wrapperRef, so include it in the
  // click-outside refs to keep clicks inside the dropdown from closing it.
  useClickOutside(
    [wrapperRef, dropdownRef],
    () => {
      closeTimeoutRef.current = setTimeout(closeDropdown, 0)
    },
    isManagedOpen && isInternalOpen
  )
  useEffect(() => () => clearTimeout(closeTimeoutRef.current), [])

  // Portal mode: track the input's viewport position so the fixed dropdown
  // stays anchored to it through scroll/resize.
  const [portalPos, setPortalPos] = useState<{
    top: number
    left: number
    width: number
  } | null>(null)
  useLayoutEffect(() => {
    if (!usePortal || !isDropdownOpen) {
      setPortalPos(null)
      return
    }
    const update = () => {
      if (wrapperRef.current) {
        const rect = wrapperRef.current.getBoundingClientRect()
        setPortalPos({ top: rect.bottom, left: rect.left, width: rect.width })
      }
    }
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [usePortal, isDropdownOpen])

  const handleToggle = () => {
    if (disabled) return
    if (isManagedOpen) {
      if (isInternalOpen) {
        closeDropdown()
      } else {
        setIsInternalOpen(true)
      }
    }
    onToggle?.()
  }

  // In searchable mode, selected holds option values — resolve display labels.
  // Labels are cached across renders so chips keep their labels even when the
  // current `options` list has been narrowed by a search query and no longer
  // contains a selected value.
  const labelCacheRef = useRef<Map<string, string>>(new Map())
  if (isManagedOpen) {
    options.forEach((o) => labelCacheRef.current.set(o.value, o.label))
    selectedOptions.forEach((o) => labelCacheRef.current.set(o.value, o.label))
    if (defaultOption) {
      labelCacheRef.current.set(defaultOption.value, defaultOption.label)
    }
  }
  const chipItems: MultiSelectOption[] = selected.map((value) => ({
    value,
    label: isManagedOpen ? labelCacheRef.current.get(value) ?? value : value,
  }))

  const { visibleCount, refCallback } = useOverflowCount(
    selected.length,
    CHIP_GAP,
    JSON.stringify(selected)
  )

  const hiddenCount =
    visibleCount !== undefined ? selected.length - visibleCount : 0

  // While the default (select-all) option is selected, every option in the
  // dropdown displays as checked — though only the default option's chip
  // shows in the input.
  const isDefaultOptionSelected =
    !!defaultOption && selected.includes(defaultOption.value)

  const checkboxOptions: CheckboxOption[] = searchable
    ? options.map((opt) => ({
        value: opt.value,
        label: opt.label,
        subtitle: opt.subtitle,
        caption: opt.caption,
        isSelected: isDefaultOptionSelected || selected.includes(opt.value),
      }))
    : []

  const input = (
    <Box
      {...containerStyles(colorScheme, disabled)}
      aria-disabled={disabled || undefined}
      aria-expanded={isDropdownOpen}
      data-testid={dataTestId}
      onClick={handleToggle}
      onKeyDown={(e) => {
        if (disabled) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleToggle()
        }
      }}
      role='button'
      tabIndex={disabled ? -1 : 0}
    >
      {label && (
        <Box as='span' {...inputLabelStyles(colorScheme)}>
          {label}
        </Box>
      )}

      <Box {...inputRowStyles}>
        <Box
          ref={refCallback as React.Ref<HTMLDivElement>}
          {...chipsAreaStyles}
        >
          {chipItems.length === 0 ? (
            <Box as='span' {...placeholderStyles(colorScheme)}>
              {placeholder}
            </Box>
          ) : (
            chipItems.map((item, i) => {
              const isHidden = visibleCount !== undefined && i >= visibleCount
              return (
                <chakra.span
                  data-overflow-item=''
                  key={item.value}
                  style={chipWrapperStyle(isHidden)}
                >
                  <Chip
                    colorScheme={colorScheme}
                    label={item.label}
                    onDismiss={
                      disabled ? undefined : () => onRemove(item.value)
                    }
                    variant='field'
                  />
                </chakra.span>
              )
            })
          )}

          {/* Always mounted so its width can be measured; hidden when not truncated */}
          <OverflowBadge colorScheme={colorScheme} hiddenCount={hiddenCount} />
        </Box>
      </Box>

      {/* Absolutely positioned so it stays vertically centered in the full
          container regardless of whether a label is present above the chips */}
      <Box {...controlsStyles} onClick={(e) => e.stopPropagation()}>
        {selected.length > 0 && !disabled && (
          <chakra.button
            aria-label='Clear all'
            onClick={(e) => {
              e.stopPropagation()
              onClear()
            }}
            type='button'
            {...controlButtonStyles(colorScheme)}
          >
            <CloseIcon colorScheme={colorScheme} size={14} />
          </chakra.button>
        )}
        <chakra.button
          aria-label={isDropdownOpen ? 'Close dropdown' : 'Open dropdown'}
          onClick={(e) => {
            e.stopPropagation()
            handleToggle()
          }}
          type='button'
          {...controlButtonStyles(colorScheme)}
          style={chevronStyle(isDropdownOpen)}
        >
          <ChevronDownIcon height={24} width={24} />
        </chakra.button>
      </Box>
    </Box>
  )

  if (!isManagedOpen) return input

  return (
    <Box
      onKeyDown={(e) => {
        if (e.key === 'Escape') closeDropdown()
      }}
      position='relative'
      ref={wrapperRef}
      width='100%'
    >
      {input}
      {isDropdownOpen &&
        (() => {
          const dropdown = (
            <Box
              bg={
                colorScheme === 'light'
                  ? 'light.bg.card-elevated'
                  : 'bg.card-elevated'
              }
              border='1px solid'
              borderColor={
                colorScheme === 'light'
                  ? 'light.border.subtle'
                  : 'border.subtle'
              }
              boxShadow='dropdown'
              display='flex'
              flexDirection='column'
              // Panel content can host its own popovers (e.g. the DatePicker
              // calendar) that must escape the dropdown box; the searchable
              // list scrolls internally and stays clipped.
              overflow={hasPanel ? 'visible' : 'hidden'}
              ref={dropdownRef}
              {...(usePortal
                ? {
                    'data-overlay-root': true,
                    position: 'fixed',
                    top: `${portalPos?.top ?? 0}px`,
                    left: `${portalPos?.left ?? 0}px`,
                    width: `${portalPos?.width ?? 0}px`,
                    mt: '4px',
                    visibility: portalPos ? 'visible' : 'hidden',
                    zIndex: 1500,
                  }
                : {
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    zIndex: 10,
                    ...(dropdownPlacement === 'top'
                      ? { bottom: '100%', mb: '4px' }
                      : { top: '100%' }),
                  })}
            >
              {hasPanel ? (
                panelContent
              ) : (
                <>
                  {intentLabel && (
                    <Box {...primaryActionHeaderStyles(colorScheme)}>
                      {intentLabel}
                    </Box>
                  )}
                  <TypeaheadSearch
                    alwaysOpen
                    autoFocus
                    checkboxOptions={checkboxOptions}
                    colorScheme={colorScheme}
                    // The results header is position: sticky; without an
                    // explicit bg it falls back to transparent in the dark
                    // scheme and option rows scroll through it. Match the
                    // dropdown surface painted on the wrapper Box above.
                    dropdownBg={tokenVar(
                      colorScheme === 'light'
                        ? 'colors.light.bg.card-elevated'
                        : 'colors.bg.card-elevated'
                    )}
                    filterMode={filterMode}
                    flatDropdown
                    headerContent={
                      defaultOption && (
                        <>
                          {defaultOptionHeader && (
                            <Box {...primaryActionHeaderStyles(colorScheme)}>
                              {defaultOptionHeader}
                            </Box>
                          )}
                          <Box
                            onClick={() =>
                              onToggleOption?.(defaultOption.value)
                            }
                            {...checkboxOptionRowStyles(
                              isDefaultOptionSelected,
                              colorScheme
                            )}
                          >
                            <Box
                              as='span'
                              {...checkboxStyles(
                                isDefaultOptionSelected,
                                colorScheme
                              )}
                            />
                            {defaultOption.label}
                          </Box>
                        </>
                      )
                    }
                    height={28}
                    maxDropdownHeight={maxDropdownHeight}
                    maxResults={maxResults}
                    noMatchLabel={noMatchLabel}
                    onQueryChange={onQueryChange}
                    onToggle={onToggleOption}
                    options={[]}
                    placeholder={searchPlaceholder}
                    resultsHeader={resultsHeader}
                    showResultsWhenEmpty
                    width='100%'
                  />
                </>
              )}
            </Box>
          )
          return usePortal && typeof document !== 'undefined'
            ? ReactDOM.createPortal(dropdown, document.body)
            : dropdown
        })()}
    </Box>
  )
}
